import { Skeleton } from "@mui/material";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { appUrl } from "Utils/UrlUtils";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { UserAccountDetail } from "Components/UserAccount/UserAccountDetail";

export const AccountPage = () => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const userRole = useCurrentUserRole();

  const { data: verificationData, isLoading: isVerificationLoading } =
    useVerificationsQuery(user?.userID || undefined);

  const { data: partyData, isLoading: isPartyLoading } = usePartyQuery(
    user?.partyPublicID,
  );

  const isAllDataReady = partyData && verificationData;
  const isLoading = isVerificationLoading || isPartyLoading;
  const isError = !user || !user.partyPublicID || !isAllDataReady || !userRole;

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Pages.Titles.UserAccount)}>
        <StyledPageWrapper>
          <div className="left-content">
            <Skeleton height={48} variant="rounded" sx={{ mt: "0.25rem" }} />
            <Skeleton height={400} variant="rounded" sx={{ mt: "0.5rem" }} />
          </div>
        </StyledPageWrapper>
      </AuthenticatedLayout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <ErrorText>{t(Resources.Pages.Content.Account.ProfileError)}</ErrorText>
      </Layout>
    );
  }

  return (
    <Layout>
      <UserAccountDetail
        userRole={userRole}
        party={partyData}
        verification={{
          ...verificationData,
          requestedUserID: verificationData.userID,
        }}
      />
    </Layout>
  );
};

const Layout = ({ children }: { children: ReactNode }) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.UserAccount)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("settings"),
      }}
    >
      <StyledPageWrapper>
        <div className="left-content">{children}</div>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default AccountPage;
